<script setup>
import { computed, ref } from 'vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Analise Ganhos vs Perdidos',
  },
});

const activeView = ref('summary');

function formatCurrency(value) {
  if (!value) return 'R$ 0';
  if (value >= 1000000) return `R$ ${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `R$ ${(value / 1000).toFixed(1)}K`;
  return `R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 0 })}`;
}

function formatNumber(num) {
  if (!num) return '0';
  return num.toLocaleString('pt-BR');
}

const wonData = computed(() => props.data?.won || {});
const lostData = computed(() => props.data?.lost || {});
const comparison = computed(() => props.data?.comparison || {});
const lostReasons = computed(() => props.data?.lost_reasons || []);
const timeToClose = computed(() => props.data?.time_to_close || {});

const totalDeals = computed(() => {
  return (wonData.value.count || 0) + (lostData.value.count || 0);
});

const winRate = computed(() => {
  if (!totalDeals.value) return 0;
  return ((wonData.value.count || 0) / totalDeals.value) * 100;
});
</script>

<template>
  <div class="rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center gap-2">
        <span class="i-lucide-scale text-lg text-n-amber-10" />
        <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
      </div>
      <div class="flex gap-1">
        <button
          class="px-2 py-1 text-xs rounded transition-colors"
          :class="
            activeView === 'summary'
              ? 'bg-n-blue-9 text-white'
              : 'bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2'
          "
          @click="activeView = 'summary'"
        >
          Resumo
        </button>
        <button
          class="px-2 py-1 text-xs rounded transition-colors"
          :class="
            activeView === 'reasons'
              ? 'bg-n-blue-9 text-white'
              : 'bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2'
          "
          @click="activeView = 'reasons'"
        >
          Motivos
        </button>
        <button
          class="px-2 py-1 text-xs rounded transition-colors"
          :class="
            activeView === 'timing'
              ? 'bg-n-blue-9 text-white'
              : 'bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2'
          "
          @click="activeView = 'timing'"
        >
          Tempo
        </button>
      </div>
    </div>

    <div v-if="loading" class="flex items-center justify-center h-48">
      <Spinner size="large" />
    </div>

    <!-- Summary View -->
    <div v-else-if="activeView === 'summary'" class="flex flex-col gap-4">
      <!-- Win Rate Bar -->
      <div class="flex flex-col gap-2">
        <div class="flex items-center justify-between text-sm">
          <span class="text-n-teal-11 font-medium">Ganhos: {{ formatNumber(wonData.count) }}</span>
          <span class="text-n-ruby-11 font-medium">Perdidos: {{ formatNumber(lostData.count) }}</span>
        </div>
        <div class="h-4 flex rounded-full overflow-hidden">
          <div
            class="bg-n-teal-9 transition-all duration-300"
            :style="{ width: `${winRate}%` }"
          />
          <div
            class="bg-n-ruby-9 transition-all duration-300"
            :style="{ width: `${100 - winRate}%` }"
          />
        </div>
        <div class="text-center text-sm text-n-slate-11">
          Taxa de conversao:
          <span class="font-bold text-n-slate-12">{{ winRate.toFixed(1) }}%</span>
        </div>
      </div>

      <!-- Won vs Lost Comparison -->
      <div class="grid grid-cols-2 gap-4">
        <!-- Won -->
        <div class="p-4 rounded-lg bg-n-teal-2 border border-n-teal-6">
          <div class="flex items-center gap-2 mb-3">
            <span class="i-lucide-trophy text-n-teal-11" />
            <span class="font-medium text-n-teal-12">Ganhos</span>
          </div>
          <div class="flex flex-col gap-2">
            <div class="flex justify-between">
              <span class="text-xs text-n-teal-11">Total</span>
              <span class="text-sm font-bold text-n-teal-12">{{
                formatNumber(wonData.count)
              }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-xs text-n-teal-11">Valor</span>
              <span class="text-sm font-bold text-n-teal-12">{{
                formatCurrency(wonData.total_value)
              }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-xs text-n-teal-11">Ticket Medio</span>
              <span class="text-sm font-bold text-n-teal-12">{{
                formatCurrency(wonData.avg_value)
              }}</span>
            </div>
          </div>
        </div>

        <!-- Lost -->
        <div class="p-4 rounded-lg bg-n-ruby-2 border border-n-ruby-6">
          <div class="flex items-center gap-2 mb-3">
            <span class="i-lucide-x-circle text-n-ruby-11" />
            <span class="font-medium text-n-ruby-12">Perdidos</span>
          </div>
          <div class="flex flex-col gap-2">
            <div class="flex justify-between">
              <span class="text-xs text-n-ruby-11">Total</span>
              <span class="text-sm font-bold text-n-ruby-12">{{
                formatNumber(lostData.count)
              }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-xs text-n-ruby-11">Valor Perdido</span>
              <span class="text-sm font-bold text-n-ruby-12">{{
                formatCurrency(lostData.total_value)
              }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-xs text-n-ruby-11">Ticket Medio</span>
              <span class="text-sm font-bold text-n-ruby-12">{{
                formatCurrency(lostData.avg_value)
              }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Comparison Stats -->
      <div class="grid grid-cols-3 gap-3">
        <div class="p-3 rounded-lg bg-n-alpha-1 text-center">
          <div class="text-xs text-n-slate-10 mb-1">Diferenca Valor</div>
          <div
            class="text-lg font-bold"
            :class="
              comparison.value_difference >= 0
                ? 'text-n-teal-11'
                : 'text-n-ruby-11'
            "
          >
            {{ comparison.value_difference >= 0 ? '+' : ''
            }}{{ formatCurrency(comparison.value_difference) }}
          </div>
        </div>
        <div class="p-3 rounded-lg bg-n-alpha-1 text-center">
          <div class="text-xs text-n-slate-10 mb-1">Diferenca Ticket</div>
          <div
            class="text-lg font-bold"
            :class="
              comparison.avg_difference >= 0
                ? 'text-n-teal-11'
                : 'text-n-ruby-11'
            "
          >
            {{ comparison.avg_difference >= 0 ? '+' : ''
            }}{{ formatCurrency(comparison.avg_difference) }}
          </div>
        </div>
        <div class="p-3 rounded-lg bg-n-alpha-1 text-center">
          <div class="text-xs text-n-slate-10 mb-1">Oportunidade Perdida</div>
          <div class="text-lg font-bold text-n-amber-11">
            {{ formatCurrency(lostData.total_value) }}
          </div>
        </div>
      </div>
    </div>

    <!-- Lost Reasons View -->
    <div v-else-if="activeView === 'reasons'" class="flex flex-col gap-3">
      <div
        v-if="lostReasons.length === 0"
        class="text-center text-n-slate-11 py-8"
      >
        Nenhum motivo de perda registrado
      </div>
      <div
        v-for="reason in lostReasons"
        :key="reason.reason"
        class="flex items-center justify-between p-3 rounded-lg bg-n-alpha-1"
      >
        <div class="flex items-center gap-3">
          <span class="i-lucide-alert-circle text-n-ruby-10" />
          <div class="flex flex-col">
            <span class="text-sm font-medium text-n-slate-12">{{
              reason.reason || 'Nao especificado'
            }}</span>
            <span class="text-xs text-n-slate-11">{{ formatNumber(reason.count) }} negocios</span>
          </div>
        </div>
        <div class="flex flex-col items-end">
          <span class="text-sm font-bold text-n-ruby-11">{{
            formatCurrency(reason.total_value)
          }}</span>
          <span class="text-xs text-n-slate-10">{{ reason.percentage?.toFixed(1) || 0 }}%</span>
        </div>
      </div>
    </div>

    <!-- Timing View -->
    <div v-else-if="activeView === 'timing'" class="flex flex-col gap-4">
      <div class="grid grid-cols-2 gap-4">
        <!-- Won Timing -->
        <div class="p-4 rounded-lg bg-n-teal-2 border border-n-teal-6">
          <div class="flex items-center gap-2 mb-3">
            <span class="i-lucide-clock text-n-teal-11" />
            <span class="font-medium text-n-teal-12">Tempo p/ Ganhar</span>
          </div>
          <div class="text-2xl font-bold text-n-teal-11 mb-1">
            {{ timeToClose.won_avg_days?.toFixed(0) || 0 }} dias
          </div>
          <div class="text-xs text-n-teal-11">
            Min: {{ timeToClose.won_min_days || 0 }}d | Max:
            {{ timeToClose.won_max_days || 0 }}d
          </div>
        </div>

        <!-- Lost Timing -->
        <div class="p-4 rounded-lg bg-n-ruby-2 border border-n-ruby-6">
          <div class="flex items-center gap-2 mb-3">
            <span class="i-lucide-clock text-n-ruby-11" />
            <span class="font-medium text-n-ruby-12">Tempo p/ Perder</span>
          </div>
          <div class="text-2xl font-bold text-n-ruby-11 mb-1">
            {{ timeToClose.lost_avg_days?.toFixed(0) || 0 }} dias
          </div>
          <div class="text-xs text-n-ruby-11">
            Min: {{ timeToClose.lost_min_days || 0 }}d | Max:
            {{ timeToClose.lost_max_days || 0 }}d
          </div>
        </div>
      </div>

      <div class="p-3 rounded-lg bg-n-alpha-1">
        <div class="text-xs text-n-slate-11 mb-2">Insight</div>
        <p class="text-sm text-n-slate-12">
          <template
            v-if="
              (timeToClose.won_avg_days || 0) < (timeToClose.lost_avg_days || 0)
            "
          >
            Negocios ganhos fecham em media
            <strong>{{
                (
                  (timeToClose.lost_avg_days || 0) -
                  (timeToClose.won_avg_days || 0)
                ).toFixed(0)
              }}
              dias</strong>
            mais rapido que os perdidos. Foque em acelerar o ciclo de vendas.
          </template>
          <template
            v-else-if="
              (timeToClose.won_avg_days || 0) > (timeToClose.lost_avg_days || 0)
            "
          >
            Negocios perdidos sao identificados em media
            <strong>{{
                (
                  (timeToClose.won_avg_days || 0) -
                  (timeToClose.lost_avg_days || 0)
                ).toFixed(0)
              }}
              dias</strong>
            mais rapido. Invista mais tempo em qualificacao.
          </template>
          <template v-else>
            O tempo de fechamento para negocios ganhos e perdidos e similar.
          </template>
        </p>
      </div>
    </div>
  </div>
</template>
